import React from 'react';
import { Typography } from '@/app/common/Typography';
import { cn } from '@/app/lib/utils';
import type { TextLink, LegalLink } from '../interfaces';
import Link from 'next/link';
import { theme } from '@workspace/ui/theme';

export interface FooterBottomProps {
  copyrightText: string;
  additionalText?: TextLink;
  legalLinks?: LegalLink[];
  className?: string;
}

export const FooterBottom = ({ copyrightText, additionalText, legalLinks = [], className = '' }: FooterBottomProps) => {
  const footerBackgroundClass = theme === 'redRec' ? 'bg-primary-200' : 'bg-primary';
  const textColor = theme === 'redRec' ? 'text-primary' : 'text-white';
  const getHref = (link?: any) => {
    if (!link) return undefined;

    switch (link.type) {
      case 'internal':
      case 'external':
        return link.url;
      case 'anchor':
        return link.anchor ? `#${link.anchor}` : undefined;
      default:
        return link.url || link.href;
    }
  };

  const renderAdditionalText = () => {
    if (!additionalText) return null;

    const href = getHref(additionalText.link);
    const className = cn(textColor, '!text-sm', additionalText.isBold && 'font-semibold', href && 'cursor-pointer');

    if (href) {
      return (
        <Link
          href={href}
          target={additionalText.link?.isOpenInNewTab ? '_blank' : undefined}
          rel={additionalText.link?.isOpenInNewTab ? 'noopener noreferrer' : undefined}
          className={className}
        >
          {additionalText.text}
        </Link>
      );
    }

    return (
      <Typography variant="bodyXs" className={className}>
        {additionalText.text}
      </Typography>
    );
  };

  return (
    <div className={cn(footerBackgroundClass, 'font-body', className)}>
      <div className="wrapper px-md md:px-4xl py-lg">
        <div className={cn('flex flex-col md:flex-row justify-between items-center gap-xl', textColor, 'text-sm')}>
          <div className="text-center md:text-left font-bold md:pb-0">
            <Typography
              variant="bodyXs"
              className={cn('block md:inline mb-2.5 md:mb-0 font-semibold !text-sm', textColor)}
            >
              {copyrightText.split('|')[0]}
            </Typography>
            {copyrightText.includes('|') && (
              <>
                <Typography variant="bodyXs" className={cn('hidden md:inline !text-sm', textColor)}>
                  |
                </Typography>
                <Typography variant="bodyXs" className={cn('block md:inline font-semibold !text-sm', textColor)}>
                  {copyrightText.split('|')[1]}
                </Typography>
              </>
            )}
          </div>
          <div className="flex flex-col items-center md:items-end gap-xs">
            <div>{renderAdditionalText()}</div>
            <div className="flex items-center gap-2xs">
              {legalLinks.map((link, index) => (
                <React.Fragment key={index}>
                  <Typography
                    variant="link"
                    href={link.url || '#'}
                    onClick={(e) => {
                      const hasExternalUrl = !!link.external;
                      const shouldOpenInNewTab = link.isOpenInNewTab || hasExternalUrl;
                      const hasValidHref = !!link.url && link.url !== '#';

                      if (shouldOpenInNewTab && hasValidHref) {
                        e.preventDefault();
                        window.open(link.url, '_blank', 'noopener,noreferrer');
                        return;
                      }

                      if (!hasValidHref) {
                        e.preventDefault();
                      }
                    }}
                    className={cn('hover:underline text-sm !font-normal', textColor, '!no-underline')}
                  >
                    {link.label}
                  </Typography>
                  {index < legalLinks.length - 1 && (
                    <Typography variant="bodyXs" className={cn(textColor, 'text-sm')}>
                      |
                    </Typography>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
